import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Logo } from '../Logo';
import { useAuth } from '../../contexts/AuthContext';
import { destinationAfterPlanCheck } from '../../services/planAccess';
import styles from './Header.module.css';

// The in-page sections each public page offers, in the order they appear
// down the page. Desktop and mobile navs both render from this, so a
// section only ever has to be listed once.
const SECTION_NAVS = {
  '/': [
    { id: 'home', label: 'Home' },
    { id: 'who-we-are', label: 'Who We Are' },
    { id: 'team', label: 'Team' },
    { id: 'products', label: 'Products' },
    { id: 'solutions', label: 'Solutions' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'get-in-touch', label: 'Work With Us' },
    { id: 'contact', label: 'Contact' }
  ],
  '/bi': [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'process', label: 'Process' },
    { id: 'technologies', label: 'Technologies' },
    { id: 'gallery', label: 'Gallery' }
  ],
  '/data-engineering': [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'process', label: 'Process' },
    { id: 'technologies', label: 'Technologies' }
  ],
  '/ml': [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'process', label: 'Process' },
    { id: 'technologies', label: 'Technologies' },
    { id: 'gallery', label: 'Use Cases' }
  ],
  '/strategy': [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'process', label: 'Process' },
    { id: 'technologies', label: 'Frameworks' },
    { id: 'gallery', label: 'Case Studies' }
  ]
};

/* Product pages are one route with a slug, so their sections are resolved by
   slug rather than listed per path. Only ids that the page actually renders
   are listed, since a link to a missing section falls back to the home page. */
const PRODUCT_NAVS = {
  benchmarks: [
    { id: 'overview', label: 'Overview' },
    { id: 'capabilities', label: 'How it works' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'segmentation', label: 'Segments' }
  ],
  default: [
    { id: 'overview', label: 'Overview' },
    { id: 'capabilities', label: 'What you get' },
    { id: 'pathways', label: 'Compare' }
  ]
};

const productSections = (pathname) => {
  if (!pathname.startsWith('/products/')) return null;
  const slug = pathname.slice('/products/'.length).replace(/\/$/, '');
  return PRODUCT_NAVS[slug] || PRODUCT_NAVS.default;
};

// Matches the scroll offset used when jumping to a section, so the link
// marks itself the moment its section settles under the fixed header.
const HEADER_OFFSET = 134;

/**
 * Tracks which of the page's sections is currently under the header.
 *
 * Takes the ids as a comma-joined string rather than an array so the
 * effect has a stable dependency — a fresh array every render would
 * re-subscribe the scroll listener on every render.
 */
const useActiveSection = (idsKey) => {
  const [active, setActive] = useState('');

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (ids.length === 0) {
      setActive('');
      return undefined;
    }

    let frame = null;

    const pick = () => {
      frame = null;

      // The last section whose top has passed under the header wins.
      let current = '';
      ids.forEach((id) => {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= HEADER_OFFSET + 10) {
          current = id;
        }
      });

      // A short final section can never reach that line, so at the very
      // bottom of the page mark the last section that actually exists.
      const atBottom =
        window.innerHeight + window.pageYOffset >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) {
        for (let i = ids.length - 1; i >= 0; i -= 1) {
          if (document.getElementById(ids[i])) {
            current = ids[i];
            break;
          }
        }
      }

      setActive(current || ids[0]);
    };

    const onScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(pick);
      }
    };

    pick();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [idsKey]);

  return active;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = SECTION_NAVS[location.pathname] || productSections(location.pathname) || [];
  const activeSection = useActiveSection(sections.map((s) => s.id).join(','));

  const getDisplayName = () => {
    return user?.fullName || user?.username || 'User';
  };
  const isAdmin = user?.role === 'admin';

  const handleNavigation = (path) => {
    navigate(path);
  };

  const openWorkspace = async () => {
    // ERP is the default landing place for the signed-in app. The route
    // guards entitlement itself, so no plan check is needed here.
    if (isAdmin) {
      navigate('/erp');
      return;
    }
    navigate('/erp');
  };

  const scrollToSection = (sectionId) => {
    // Scroll within the current page when the section is here. Only fall
    // back to the home page when it is not, which is what makes the
    // per-page navs on /bi, /ml, /strategy and /data-engineering work.
    const element = document.getElementById(sectionId);
    if (element) {
      const scrollPosition =
        element.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      return;
    }

    navigate('/', { state: { scrollTo: sectionId } });
  };

  // One section link, marked when its section is the one being read.
  const sectionLink = ({ id, label }, onDone) => (
    <li key={id}>
      <a
        className={activeSection === id ? styles.active : undefined}
        aria-current={activeSection === id ? 'true' : undefined}
        onClick={() => {
          scrollToSection(id);
          if (onDone) onDone();
        }}
      >
        {label}
      </a>
    </li>
  );

  const backToHome = (onDone) => (
    <li key="back">
      <a
        className={styles.backBtn}
        onClick={() => {
          handleNavigation('/');
          if (onDone) onDone();
        }}
      >
        ← Back to Home
      </a>
    </li>
  );

  const getNavLinks = () => {
    if (sections.length === 0) {
      return null;
    }

    return (
      <ul className={styles.navLinks}>
        {sections.map((section) => sectionLink(section))}
        {location.pathname === '/' ? (
          isAuthenticated ? (
            <li>
              <button onClick={openWorkspace} className={styles.profileButton}>
                <span className={styles.profileIcon}>👤</span>
                <span className={styles.profileText}>{getDisplayName()}</span>
              </button>
            </li>
          ) : (
            <li>
              <a onClick={() => handleNavigation('/login')} className={styles.loginButton}>Login</a>
            </li>
          )
        ) : (
          backToHome()
        )}
      </ul>
    );
  };

  const getMobileNavLinks = () => {
    if (sections.length === 0) {
      return null;
    }

    const close = () => setIsMobileMenuOpen(false);

    return (
      <ul className={styles.mobileNavLinks}>
        {sections.map((section) => sectionLink(section, close))}
        {location.pathname === '/' ? (
          isAuthenticated ? (
            <>
              <li>
                <a onClick={() => { openWorkspace(); close(); }} className={styles.dashboardButton}>Dashboard</a>
              </li>
              <li>
                <a onClick={() => { handleNavigation('/profile'); close(); }} className={styles.dropdownItem}>Profile</a>
              </li>
              <li>
                <a onClick={() => { logout(); navigate('/'); close(); }} className={styles.dropdownItem}>Logout</a>
              </li>
            </>
          ) : (
            <li>
              <a onClick={() => { handleNavigation('/login'); close(); }} className={styles.loginButton}>Login</a>
            </li>
          )
        ) : (
          backToHome(close)
        )}
      </ul>
    );
  };

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <div className={styles.logoContainer}>
          <div className={styles.logo}>
            <Logo size="full" />
          </div>
        </div>

        <div className={styles.navRightContainer}>
          {/* Mobile menu toggle */}
          <button
            type="button"
            className={styles.menuToggle}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-controls="primary-navigation"
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((v) => !v)}
          >
            <span className={styles.burger} />
            <span className={styles.burger} />
            <span className={styles.burger} />
          </button>

          <div className={styles.navLinksContainer}>
            {getNavLinks()}
          </div>
        </div>
      </nav>
      {/* Mobile navigation drawer */}
      {isMobileMenuOpen && (
        <div className={styles.mobileMenu} role="dialog" aria-modal="true">
          <nav id="primary-navigation" aria-label="Primary">
            {getMobileNavLinks()}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
